import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import Plate from '../components/Plate';
import DishSheet, { DISH_CATEGORIES, categoryLabel, eur, isFood, unitLabel } from '../components/DishSheet';
import {
    Plus, Trash2, Edit, X, Search,
    ChefHat, ChevronLeft, ChevronRight, ExternalLink, AlertTriangle
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import config from '../config';
import { searchKey, suggestedPrice } from '../lib/utils';


// The kinds of things Calum sells. Food is what the target food cost applies to.
const GROUPS = [
    { key: 'dishes', label: 'Plats', categories: ['main', 'starter'] },
    { key: 'pastry', label: 'Pâtisseries', categories: ['dessert'] },
    { key: 'extras', label: 'Suppléments', categories: ['side'] },
    { key: 'drinks', label: 'Boissons', categories: ['beverage'] },
    { key: 'other', label: 'Autres', categories: ['other'] },
];
const inGroup = (dish, key) => GROUPS.find((g) => g.key === key)?.categories.includes(dish.category);

/**
 * Dishes page - create and manage dishes with ingredients
 */
const Dishes = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dishes, setDishes] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'main',
        selling_price: '',
        vat_rate: 12,
        is_finished_product: false,
        odoo_cost_price: '',
        portions: 1,
        active: true,
        ingredients: []
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    // Triage controls
    const [sortBy, setSortBy] = useState('color'); // 'color' | 'margin' | 'name'
    const [group, setGroup] = useState('dishes');
    const [needsRecipeOnly, setNeedsRecipeOnly] = useState(false);
    const [incompleteOnly, setIncompleteOnly] = useState(false);
    const [needsPriceOnly, setNeedsPriceOnly] = useState(false);
    const [targetFoodCost, setTargetFoodCost] = useState(30);
    const [targetInput, setTargetInput] = useState('30');

    // Read-only detail / cost-breakdown modal
    const [detailDish, setDetailDish] = useState(null);

    // For ingredient selection
    const [ingredientSearch, setIngredientSearch] = useState('');
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [ingredientQuantity, setIngredientQuantity] = useState('');
    const [ingredientUnit, setIngredientUnit] = useState('kg');

    const categories = DISH_CATEGORIES;

    useEffect(() => {
        document.title = 'Plats | Costy';
        fetchDishes();
        fetchIngredients();
        axiosInstance.get('/costing-settings/')
            .then(({ data }) => {
                const target = parseFloat(data.target_food_cost);
                setTargetFoodCost(target);
                setTargetInput(String(target));
            })
            .catch((error) => console.error('Failed to fetch costing settings:', error));
    }, []);

    // "Modifier" on the dashboard's dish sheet lands here (/dishes?edit=12): open the form.
    const editDishId = searchParams.get('edit');
    useEffect(() => {
        if (!editDishId || loading) return;
        const linked = dishes.find((d) => String(d.id) === editDishId);
        if (linked) {
            const linkedGroup = GROUPS.find((g) => inGroup(linked, g.key));
            if (linkedGroup) setGroup(linkedGroup.key);
            handleEdit(linked);
        }
        setSearchParams({}, { replace: true });
    }, [editDishId, loading, dishes, setSearchParams]); // eslint-disable-line

    // Reset to page 1 when search/sort/filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortBy, needsRecipeOnly, incompleteOnly, needsPriceOnly, group]);

    const fetchDishes = async () => {
        try {
            const response = await axiosInstance.get('/dishes/');
            const data = response.data;
            setDishes(Array.isArray(data) ? data : data?.results ?? []);
        } catch (error) {
            console.error('Failed to fetch dishes:', error);
            setDishes([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchIngredients = async () => {
        try {
            const response = await axiosInstance.get('/ingredients/?active=true');
            const data = response.data;
            setIngredients(Array.isArray(data) ? data : data?.results ?? []);
        } catch (error) {
            console.error('Failed to fetch ingredients:', error);
            setIngredients([]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                selling_price: parseFloat(formData.selling_price) || 0,
                vat_rate: parseFloat(formData.vat_rate) || 0,
                portions: Math.max(1, parseInt(formData.portions) || 1),
                // Fixed cost only applies to finished products; null otherwise
                odoo_cost_price: formData.is_finished_product ? (parseFloat(formData.odoo_cost_price) || 0) : null,
                // Ready-to-sell items can carry a single linked item too; keep them.
                ingredients: formData.ingredients.map(ing => ({
                    ingredient_id: ing.ingredient_id,
                    quantity: parseFloat(ing.quantity),
                    unit: ing.unit
                }))
            };

            if (editingId) {
                await axiosInstance.put(`/dishes/${editingId}/`, payload);
            } else {
                await axiosInstance.post('/dishes/', payload);
            }

            setShowForm(false);
            resetForm();
            fetchDishes();
        } catch (error) {
            console.error('Failed to save dish:', error);
            alert('Enregistrement impossible.');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            category: 'main',
            selling_price: '',
            vat_rate: 12,
            is_finished_product: false,
            odoo_cost_price: '',
            portions: 1,
            active: true,
            ingredients: []
        });
        setEditingId(null);
        setSelectedIngredient(null);
        setIngredientQuantity('');
        setIngredientSearch('');
    };

    const handleEdit = (dish) => {
        setFormData({
            name: dish.name,
            description: dish.description || '',
            category: dish.category,
            selling_price: dish.selling_price,
            vat_rate: parseFloat(dish.vat_rate ?? 12),
            is_finished_product: dish.is_finished_product ?? false,
            odoo_cost_price: dish.odoo_cost_price ?? '',
            portions: dish.portions ?? 1,
            active: dish.active,
            ingredients: dish.dish_ingredients?.map(di => ({
                ingredient_id: di.ingredient,
                ingredient_name: di.ingredient_name,
                ingredient_unit: di.ingredient_unit,
                is_preparation: di.ingredient_is_preparation,
                quantity_per_unit: di.ingredient_quantity_per_unit || 1,
                quantity: parseFloat(di.quantity),
                unit: di.unit,
                unit_price: di.ingredient_unit_price
            })) || []
        });
        setEditingId(dish.id);
        setShowForm(true);
    };

    const saveTarget = async () => {
        const target = parseFloat(targetInput.replace(',', '.'));
        if (!(target >= 1 && target <= 100)) {
            setTargetInput(String(targetFoodCost));
            return;
        }
        if (target === targetFoodCost) return;
        setTargetFoodCost(target);
        try {
            await axiosInstance.patch('/costing-settings/', { target_food_cost: target });
        } catch (error) {
            console.error('Failed to save target food cost:', error);
            alert('Enregistrement impossible.');
        }
    };

    const usePrice = async (dish, price) => {
        try {
            await axiosInstance.patch(`/dishes/${dish.id}/`, { selling_price: price });
            setDetailDish(null);
            fetchDishes();
        } catch (error) {
            console.error('Failed to set selling price:', error);
            alert('Enregistrement impossible.');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm('Supprimer ce plat ?')) return;
        try {
            await axiosInstance.delete(`/dishes/${id}/`);
            fetchDishes();
        } catch (error) {
            console.error('Failed to delete dish:', error);
        }
    };

    const addIngredientToDish = () => {
        if (!selectedIngredient || !ingredientQuantity) return;

        const exists = formData.ingredients.find(
            i => i.ingredient_id === selectedIngredient.id
        );
        if (exists) {
            alert('Cet ingrédient est déjà dans la recette : modifiez sa quantité.');
            return;
        }

        setFormData({
            ...formData,
            ingredients: [
                ...formData.ingredients,
                {
                    ingredient_id: selectedIngredient.id,
                    ingredient_name: selectedIngredient.name,
                    ingredient_unit: selectedIngredient.unit,
                    is_preparation: selectedIngredient.is_preparation,
                    quantity_per_unit: selectedIngredient.quantity_per_unit || 1,
                    quantity: ingredientQuantity,
                    unit: ingredientUnit,
                    unit_price: selectedIngredient.unit_price
                }
            ]
        });

        setSelectedIngredient(null);
        setIngredientQuantity('');
        setIngredientSearch('');
    };

    const removeIngredientFromDish = (ingredientId) => {
        setFormData({
            ...formData,
            ingredients: formData.ingredients.filter(i => i.ingredient_id !== ingredientId)
        });
    };

    // --- Inline ingredient editing (purchase unit / net content) ---
    const [editingIngId, setEditingIngId] = useState(null);
    const [ingEdit, setIngEdit] = useState({ type: '', unit: '', quantity_per_unit: 1 });
    const [savingIng, setSavingIng] = useState(false);

    const openIngredientEdit = (line) => {
        const master = ingredients.find(i => i.id === line.ingredient_id) || {};
        setIngEdit({
            type: master.type || 'quantity',
            unit: line.ingredient_unit || master.unit || 'unit',
            quantity_per_unit: line.quantity_per_unit || master.quantity_per_unit || 1,
        });
        setEditingIngId(line.ingredient_id);
    };

    const saveIngredientEdit = async (ingredientId) => {
        setSavingIng(true);
        try {
            const { data } = await axiosInstance.patch(`/ingredients/${ingredientId}/`, ingEdit);
            // Update the master list and any recipe lines that use this ingredient
            setIngredients(prev => prev.map(i => i.id === ingredientId ? data : i));
            setFormData(fd => ({
                ...fd,
                ingredients: fd.ingredients.map(l => l.ingredient_id === ingredientId ? {
                    ...l,
                    ingredient_unit: data.unit,
                    quantity_per_unit: data.quantity_per_unit,
                    unit_price: data.unit_price,
                } : l),
            }));
            // Keep the selected (not-yet-added) ingredient in sync too
            setSelectedIngredient(prev => (prev && prev.id === ingredientId ? data : prev));
            setEditingIngId(null);
        } catch (e) {
            console.error('Failed to update ingredient:', e);
            alert('Enregistrement impossible.');
        } finally {
            setSavingIng(false);
        }
    };

    // Reusable inline editor for an ingredient's type / purchase unit / net content
    const renderUnitEditor = (ingredientId, ingredientName) => (
        editingIngId === ingredientId ? (
            <div className="border-t border-border pt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                    Modifier « {ingredientName} » : s’applique à tous les plats qui l’utilisent
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                    <div>
                        <label className="block text-xs text-muted-foreground mb-1">Type</label>
                        <select
                            value={ingEdit.type}
                            onChange={(e) => setIngEdit({ ...ingEdit, type: e.target.value })}
                            className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm"
                        >
                            <option value="weight">Poids</option>
                            <option value="volume">Volume</option>
                            <option value="quantity">Pièces</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-muted-foreground mb-1">Unité d’achat</label>
                        <select
                            value={ingEdit.unit}
                            onChange={(e) => setIngEdit({ ...ingEdit, unit: e.target.value })}
                            className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm"
                        >
                            {['kg', 'g', 'l', 'cl', 'ml', 'unit'].map(u => <option key={u} value={u}>{unitLabel(u)}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-muted-foreground mb-1">Contenu par unité</label>
                        <input
                            type="number"
                            step="0.0001"
                            value={ingEdit.quantity_per_unit}
                            onChange={(e) => setIngEdit({ ...ingEdit, quantity_per_unit: e.target.value })}
                            className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm"
                        />
                    </div>
                    <div className="flex gap-1">
                        <button
                            type="button"
                            onClick={() => saveIngredientEdit(ingredientId)}
                            disabled={savingIng}
                            className="flex-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-sm disabled:opacity-50"
                        >
                            {savingIng ? '…' : 'Enregistrer'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditingIngId(null)}
                            className="px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-accent"
                        >
                            Annuler
                        </button>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">
                    Astuce : pour un sac de farine de 25&nbsp;kg facturé à l’unité, choisissez Poids, kg et contenu 25, puis comptez en grammes dans la recette.
                </p>
            </div>
        ) : null
    );

    const calculateIngredientCost = (ing) => {
        try {
            const qty = parseFloat(ing.quantity || 0);
            const purchase_price = parseFloat(ing.unit_price || 0);
            const net_content = parseFloat(ing.quantity_per_unit || 1.0);
            const pricePerBase = net_content > 0 ? purchase_price / net_content : purchase_price;
            const pu = ing.ingredient_unit;
            const uu = ing.unit;

            if (pu === uu) return qty * pricePerBase;

            const weightUnits = { 'kg': 1.0, 'g': 0.001 };
            if (weightUnits[pu] && weightUnits[uu]) {
                const qtyInPu = qty * (weightUnits[uu] / weightUnits[pu]);
                return qtyInPu * pricePerBase;
            }

            const volumeUnits = { 'l': 1.0, 'cl': 0.01, 'ml': 0.001 };
            if (volumeUnits[pu] && volumeUnits[uu]) {
                const qtyInPu = qty * (volumeUnits[uu] / volumeUnits[pu]);
                return qtyInPu * pricePerBase;
            }

            return qty * pricePerBase;
        } catch (e) {
            return 0;
        }
    };

    const calculateTotalCost = () => {
        return formData.ingredients.reduce((total, ing) => {
            return total + calculateIngredientCost(ing);
        }, 0);
    };


    // Triage stats over the chosen group. A search looks through every group.
    const searching = searchTerm.trim() !== '';
    const stats = dishes.filter((d) => inGroup(d, group)).reduce((acc, d) => {
        const hasRecipe = d.dish_ingredients?.length > 0;
        const cost = parseFloat(d.effective_cost || 0);
        const missingCost = hasRecipe && d.dish_ingredients.some(di => parseFloat(di.ingredient_unit_price ?? 0) === 0);
        const isFixed = d.is_finished_product && !hasRecipe;
        const noRecipe = !hasRecipe && !d.is_finished_product;
        const priced = parseFloat(d.selling_price || 0) > 0;
        const done = priced && ((hasRecipe && !missingCost) || (d.is_finished_product && cost > 0));
        acc.total += 1;
        if (hasRecipe && !priced) acc.noPrice += 1;
        if (noRecipe) acc.noRecipe += 1;
        if (missingCost) acc.incomplete += 1;
        if (isFixed) acc.fixed += 1;
        if (done) acc.ready += 1;
        return acc;
    }, { total: 0, noRecipe: 0, noPrice: 0, incomplete: 0, fixed: 0, ready: 0 });

    const filteredDishes = dishes
        .filter(dish => {
            const q = searchKey(searchTerm);
            const matchesSearch = searchKey(dish.name).includes(q) ||
                searchKey(categoryLabel(dish.category)).includes(q) ||
                searchKey(dish.pos_category).includes(q);
            const dHasRecipe = dish.dish_ingredients?.length > 0;
            const dMissingCost = dHasRecipe && dish.dish_ingredients.some(di => parseFloat(di.ingredient_unit_price ?? 0) === 0);
            const matchesRecipe = !needsRecipeOnly || (!dHasRecipe && !dish.is_finished_product);
            const matchesIncomplete = !incompleteOnly || dMissingCost;
            const matchesPrice = !needsPriceOnly || (dHasRecipe && !(parseFloat(dish.selling_price) > 0));
            const matchesGroup = searching || inGroup(dish, group);
            return matchesGroup && matchesSearch && matchesRecipe && matchesIncomplete && matchesPrice;
        })
        .sort((a, b) => {
            if (sortBy === 'name') return a.name.localeCompare(b.name);
            if (sortBy === 'margin') {
                // Lowest real margin first; dishes without a margin yet go last
                const known = (d) => parseFloat(d.selling_price) > 0 && parseFloat(d.effective_cost) > 0;
                if (known(a) !== known(b)) return known(a) ? -1 : 1;
                return parseFloat(a.profit_margin || 0) - parseFloat(b.profit_margin || 0);
            }
            // Default: group by button color, then alphabetically (uncolored last)
            const ca = a.button_color || '￿';
            const cb = b.button_color || '￿';
            if (ca !== cb) return ca.localeCompare(cb);
            return a.name.localeCompare(b.name);
        });

    // Pagination
    const totalPages = Math.ceil(filteredDishes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedDishes = filteredDishes.slice(startIndex, startIndex + itemsPerPage);

    const filteredIngredientOptions = ingredients.filter(ing =>
        searchKey(ing.name).includes(searchKey(ingredientSearch))
    );

    const getMarginColor = (margin) => {
        if (margin >= 60) return 'text-good';
        if (margin >= 40) return 'text-warn';
        return 'text-bad';
    };

    // Map Lightspeed POS button color names to hex values
    const BUTTON_COLORS = {
        BROWN: '#8B5E3C',
        BLUE: '#3B82F6',
        LIGHT_RED: '#F87171',
        ORANGE: '#F97316',
        YELLOW: '#FACC15',
        WHITE: '#FFFFFF',
        GRAY: '#9CA3AF',
        PINK: '#EC4899',
        CYAN: '#06B6D4',
        DARK_GRAY: '#4B5563',
        DARK_RED: '#B91C1C',
        GREEN: '#22C55E',
        BLACK: '#111827',
        PURPLE: '#A855F7',
    };

    const getButtonColor = (name) => BUTTON_COLORS[name] || 'transparent';

    // Unit compatibility: cost conversion only works within the same dimension
    const UNIT_GROUP = { kg: 'weight', g: 'weight', l: 'volume', cl: 'volume', ml: 'volume', unit: 'quantity' };
    const unitsCompatible = (a, b) => UNIT_GROUP[a] && UNIT_GROUP[a] === UNIT_GROUP[b];

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title="Plats"
                    subtitle={`${filteredDishes.length} plats${searchTerm ? ' (filtrés)' : ''}, dont ${filteredDishes.filter(d => d.dish_ingredients?.length > 0).length} avec recette`}
                >
                    <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary">
                        <Plus className="h-4 w-4" />
                        Nouveau plat
                    </button>
                </PageHeader>

                {/* What kind of thing: food and drinks stay apart */}
                <div className="group-tabs" aria-label="Type d’article">
                    {GROUPS.map((g) => (
                        <button key={g.key} type="button" aria-pressed={!searching && group === g.key} onClick={() => { setGroup(g.key); setSearchTerm(''); }} className="group-tab">
                            {g.label} <span>{dishes.filter((d) => inGroup(d, g.key)).length}</span>
                        </button>
                    ))}
                </div>

                {/* Triage: each chip filters the list */}
                <div className="chips">
                    {[
                        { label: 'Sans recette', value: stats.noRecipe, on: needsRecipeOnly, toggle: () => { setNeedsRecipeOnly(v => !v); setIncompleteOnly(false); setNeedsPriceOnly(false); } },
                        { label: 'Prix à définir', value: stats.noPrice, on: needsPriceOnly, toggle: () => { setNeedsPriceOnly(v => !v); setNeedsRecipeOnly(false); setIncompleteOnly(false); } },
                        { label: 'Coût incomplet', value: stats.incomplete, on: incompleteOnly, toggle: () => { setIncompleteOnly(v => !v); setNeedsRecipeOnly(false); setNeedsPriceOnly(false); } },
                    ].map((chip) => (
                        <button key={chip.label} type="button" aria-pressed={chip.on} onClick={chip.toggle} className="chip">
                            {chip.label} <b>{chip.value}</b>
                        </button>
                    ))}
                    <span className="chip chip-static">Prêts <b>{stats.ready}</b></span>
                </div>

                {/* Search + sort */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Rechercher un plat ou une catégorie…"
                            aria-label="Rechercher un plat"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background text-foreground"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label htmlFor="dish-sort" className="text-sm text-muted-foreground">Trier par</label>
                        <select
                            id="dish-sort"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                        >
                            <option value="color">Couleur de caisse</option>
                            <option value="margin">Marge (la plus basse)</option>
                            <option value="name">Nom</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <label htmlFor="target-food-cost" className="text-sm text-muted-foreground">Objectif coût matière</label>
                        <input
                            id="target-food-cost"
                            type="text"
                            inputMode="decimal"
                            value={targetInput}
                            onChange={(e) => setTargetInput(e.target.value)}
                            onBlur={saveTarget}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            className="w-16 px-2 py-2 border border-border text-center text-sm"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                    </div>
                </div>

                {/* Dish Detail / Cost Breakdown Modal */}
                {detailDish && (
                    <DishSheet
                        dish={detailDish}
                        targetFoodCost={targetFoodCost}
                        onClose={() => setDetailDish(null)}
                        onDelete={() => { setDetailDish(null); handleDelete(detailDish.id); }}
                        onEdit={() => { setDetailDish(null); handleEdit(detailDish); }}
                        onUsePrice={(price) => usePrice(detailDish, price)}
                    />
                )}

                {/* Dish Form Modal */}
                {showForm && (
                    <div className="sheet-overlay">
                        <div className="sheet">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-2xl">
                                    {editingId ? 'Modifier le plat' : 'Nouveau plat'}
                                </h2>
                                <button onClick={() => setShowForm(false)} aria-label="Fermer" className="p-2 hover:bg-accent rounded-lg">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            <form
                                onSubmit={handleSubmit}
                                onKeyDown={(e) => {
                                    // Enter must never save the whole dish by accident: it adds the
                                    // ingredient being typed, and does nothing in the other fields.
                                    if (e.key !== 'Enter' || e.target.tagName !== 'INPUT') return;
                                    e.preventDefault();
                                    if (e.target.dataset.addLine && selectedIngredient && unitsCompatible(selectedIngredient.unit, ingredientUnit)) addIngredientToDish();
                                }}
                                className="space-y-4"
                            >
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Nom du plat *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                            required
                                            placeholder="ex. Banana Bread"
                                        />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Catégorie
                                        </label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        >
                                            {categories.map(cat => (
                                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Notes
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        rows={2}
                                        placeholder="Notes pour la cuisine"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Prix de vente TVAC (€)
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.selling_price}
                                            onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })}
                                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            TVA
                                        </label>
                                        <select
                                            value={formData.vat_rate}
                                            onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                        >
                                            <option value={12}>12 %</option>
                                            <option value={21}>21 %</option>
                                            <option value={6}>6 %</option>
                                            <option value={0}>0 %</option>
                                        </select>
                                    </div>
                                </div>

                                {!formData.is_finished_product && (
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Portions par recette
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={formData.portions}
                                            onChange={(e) => setFormData({ ...formData, portions: e.target.value })}
                                            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                            placeholder="1"
                                        />
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            Combien de portions vendues une recette complète donne (ex. 30 tranches). Le coût des ingrédients est divisé par ce nombre.
                                        </p>
                                    </div>
                                )}

                                <div className="flex items-center gap-6">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.active}
                                            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                            className="rounded border-border"
                                        />
                                        <span className="text-sm text-foreground">Actif</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_finished_product}
                                            onChange={(e) => setFormData({ ...formData, is_finished_product: e.target.checked })}
                                            className="rounded border-border"
                                        />
                                        <span className="text-sm text-foreground">Coût fixe manuel (si pas de recette)</span>
                                    </label>
                                </div>

                                {/* Fixed-cost fallback (ready-to-sell with no linked item) */}
                                {formData.is_finished_product && (
                                    <div className="border-t border-border pt-4">
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Coût fixe HTVA (€), si aucun article lié
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.odoo_cost_price}
                                            onChange={(e) => setFormData({ ...formData, odoo_cost_price: e.target.value })}
                                            className="w-full max-w-[12rem] px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                                            placeholder="0.00"
                                        />
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            Utilisé seulement si aucun article acheté n'est lié ci-dessous ; sinon le coût vient du produit lié.
                                        </p>
                                    </div>
                                )}
                                <>
                                {/* Ingredients / linked purchased item */}
                                <div className="border-t border-border pt-4">
                                    <h3 className="font-medium text-foreground mb-3">Ingrédients et préparations</h3>

                                    {/* Add Ingredient */}
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        <div className="relative flex-1 min-w-[150px]">
                                            <input
                                                type="text"
                                                placeholder="Chercher un ingrédient…"
                                                aria-label="Chercher un ingrédient"
                                                data-add-line="true"
                                                value={ingredientSearch}
                                                onChange={(e) => {
                                                    setIngredientSearch(e.target.value);
                                                    setSelectedIngredient(null);
                                                }}
                                                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                            />
                                            {ingredientSearch && !selectedIngredient && (
                                                <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                                                    {filteredIngredientOptions.slice(0, 10).map(ing => (
                                                        <button
                                                            key={ing.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedIngredient(ing);
                                                                setIngredientSearch(ing.name);
                                                                setIngredientUnit(ing.unit === 'unit' ? 'unit' : ing.unit);
                                                            }}
                                                            className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground group"
                                                        >
                                                            <div className="font-medium">{ing.name}{ing.is_preparation && <span className="tag ml-2 text-good">préparation</span>}</div>
                                                            <div className="text-xs text-muted-foreground group-hover:text-accent-foreground/80">
                                                                {eur(ing.unit_price)}/{unitLabel(ing.unit)}
                                                                {ing.supplier_name ? ` · ${ing.supplier_name}` : ''}
                                                                {ing.last_invoice_date ? ` · ${new Date(ing.last_invoice_date).toLocaleDateString('fr-BE')}` : ''}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <input
                                            type="number"
                                            step="any"
                                            data-add-line="true"
                                            aria-label="Quantité"
                                            placeholder="Qté"
                                            value={ingredientQuantity}
                                            onChange={(e) => setIngredientQuantity(e.target.value)}
                                            className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                        />
                                        <select
                                            value={ingredientUnit}
                                            onChange={(e) => setIngredientUnit(e.target.value)}
                                            aria-label="Unité"
                                            className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                                        >
                                            <option value="kg">kg</option>
                                            <option value="g">g</option>
                                            <option value="l">l</option>
                                            <option value="cl">cl</option>
                                            <option value="ml">ml</option>
                                            <option value="unit">pièce</option>
                                        </select>
                                        <button
                                            type="button"
                                            onClick={addIngredientToDish}
                                            disabled={!selectedIngredient || !ingredientQuantity || (selectedIngredient && !unitsCompatible(selectedIngredient.unit, ingredientUnit))}
                                            className="px-3 py-2 rounded-full bg-primary text-primary-foreground text-sm disabled:opacity-50"
                                        >
                                            Ajouter
                                        </button>
                                    </div>

                                    {/* Unit-compatibility warning */}
                                    {selectedIngredient && !unitsCompatible(selectedIngredient.unit, ingredientUnit) && (
                                        <div className="flex items-start gap-2 mb-3 text-xs text-warn">
                                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                            <span>
                                                « {selectedIngredient.name} » est au prix par <strong>{unitLabel(selectedIngredient.unit)}</strong> : impossible de le compter en <strong>{unitLabel(ingredientUnit)}</strong>, le coût serait faux. {selectedIngredient.unit === 'l' ? 'Sirop, huile, crème : comptez en ml.' : 'Choisissez une unité compatible.'}
                                            </span>
                                        </div>
                                    )}

                                    {/* Selected ingredient: provenance + inline edit (before adding) */}
                                    {selectedIngredient && (
                                        <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="text-xs text-muted-foreground">
                                                    <div className="text-sm font-medium text-foreground">{selectedIngredient.name}</div>
                                                    <div className="mt-0.5">
                                                        {eur(selectedIngredient.unit_price)}/{unitLabel(selectedIngredient.unit)}
                                                        {parseFloat(selectedIngredient.quantity_per_unit) > 1 ? ` · ${parseFloat(selectedIngredient.quantity_per_unit)} par unité (${eur(selectedIngredient.calculated_unit_price, 4)}/${unitLabel(selectedIngredient.unit)})` : ''}
                                                    </div>
                                                    <div className="mt-0.5 flex flex-wrap items-center gap-x-1">
                                                        <span>{selectedIngredient.supplier_name || (selectedIngredient.is_preparation ? 'Maison' : 'Sans fournisseur')}</span>
                                                        {selectedIngredient.last_invoice_date && (
                                                            <span>· dernière facture {new Date(selectedIngredient.last_invoice_date).toLocaleDateString('fr-BE')}</span>
                                                        )}
                                                        {selectedIngredient.last_invoice_odoo_id && config.odooUrl && (
                                                            <a
                                                                href={`${config.odooUrl}/odoo/vendor-bills/${selectedIngredient.last_invoice_odoo_id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-0.5 text-primary hover:underline"
                                                                title="Ouvrir la facture dans Odoo pour vérifier l’unité"
                                                            >
                                                                <ExternalLink className="h-3 w-3" /> voir la facture
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => editingIngId === selectedIngredient.id ? setEditingIngId(null) : openIngredientEdit({ ingredient_id: selectedIngredient.id, ingredient_unit: selectedIngredient.unit, quantity_per_unit: selectedIngredient.quantity_per_unit })}
                                                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border text-xs hover:bg-accent"
                                                >
                                                    <Edit className="h-3.5 w-3.5" />
                                                    {editingIngId === selectedIngredient.id ? 'Fermer' : 'Modifier'}
                                                </button>
                                            </div>
                                            {renderUnitEditor(selectedIngredient.id, selectedIngredient.name)}
                                            {!ingredientQuantity && (
                                                <p className="text-xs text-muted-foreground">Indiquez la quantité, puis Ajouter (ou Entrée).</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Ingredients List */}
                                    {formData.ingredients.length > 0 ? (
                                        <div className="space-y-2">
                                            {formData.ingredients.map((ing, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex flex-col gap-2 p-3 rounded-lg bg-muted/50"
                                                >
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <div className="flex-1 min-w-[9rem]">
                                                            <span className="text-sm font-medium text-foreground">
                                                                {ing.ingredient_name}
                                                            </span>
                                                            {ing.is_preparation && <span className="tag ml-2 text-good">préparation</span>}
                                                            <span className="block text-xs text-muted-foreground">
                                                                {eur(ing.unit_price)}/{unitLabel(ing.ingredient_unit)}
                                                            </span>
                                                            {!unitsCompatible(ing.ingredient_unit, ing.unit) && (
                                                                <span
                                                                    className="inline-flex items-center gap-1 ml-2 text-xs text-warn"
                                                                    title={`Prix par ${ing.ingredient_unit} — incompatible avec ${ing.unit} ; ajustez l'unité ou le prix dans les Ingrédients`}
                                                                >
                                                                    <AlertTriangle className="h-3 w-3" />
                                                                    unité incompatible
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                aria-label={`Quantité de ${ing.ingredient_name}`}
                                                                value={ing.quantity}
                                                                onChange={(e) => setFormData(fd => ({ ...fd, ingredients: fd.ingredients.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x) }))}
                                                                className="w-24 px-2 py-1 rounded-lg border border-border bg-background text-foreground text-sm text-right"
                                                            />
                                                            <select
                                                                value={ing.unit}
                                                                onChange={(e) => setFormData(fd => ({ ...fd, ingredients: fd.ingredients.map((x, i) => i === idx ? { ...x, unit: e.target.value } : x) }))}
                                                                className="px-2 py-1 rounded-lg border border-border bg-background text-foreground text-sm"
                                                            >
                                                                {['kg', 'g', 'l', 'cl', 'ml', 'unit'].map(u => <option key={u} value={u}>{unitLabel(u)}</option>)}
                                                            </select>
                                                            <span className="text-sm text-foreground w-16 text-right">
                                                                {eur(calculateIngredientCost(ing))}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                aria-label={`Retirer ${ing.ingredient_name}`}
                                                                onClick={() => removeIngredientFromDish(ing.ingredient_id)}
                                                                className="p-1 text-bad hover:bg-bad/10 rounded"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Aucun ingrédient pour l’instant
                                        </p>
                                    )}
                                </div>
                                </>

                                {/* Live cost / margin preview */}
                                {(() => {
                                    const linkedCost = calculateTotalCost();
                                    const recipeCost = linkedCost > 0 ? linkedCost : (parseFloat(formData.odoo_cost_price) || 0);
                                    const portions = formData.is_finished_product ? 1 : Math.max(1, parseInt(formData.portions) || 1);
                                    const cost = recipeCost / portions;
                                    const sellInclVat = parseFloat(formData.selling_price) || 0;
                                    const vat = parseFloat(formData.vat_rate) || 0;
                                    const sellExVat = sellInclVat / (1 + vat / 100);
                                    const margin = sellExVat > 0 ? ((sellExVat - cost) / sellExVat) * 100 : 0;
                                    const marginColor = margin >= 60 ? 'text-good' : margin >= 40 ? 'text-warn' : 'text-bad';
                                    return (
                                        <div className="grid grid-cols-3 gap-3 rounded-lg border border-border bg-muted/30 p-4">
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground">{formData.is_finished_product ? 'Coût fixe' : (portions > 1 ? 'Coût / portion' : 'Coût')}</p>
                                                <p className="mt-1 text-lg font-bold text-foreground">{eur(cost)}</p>
                                                {!formData.is_finished_product && portions > 1 && (
                                                    <p className="text-xs text-muted-foreground">recette {eur(recipeCost)} · {portions} portions</p>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground">Prix HTVA</p>
                                                <p className="mt-1 text-lg font-bold text-foreground">{sellExVat > 0 ? eur(sellExVat) : '—'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-muted-foreground">Marge</p>
                                                <p className={`mt-1 text-lg font-bold ${sellExVat > 0 ? marginColor : 'text-muted-foreground'}`}>
                                                    {sellExVat > 0 ? `${margin.toFixed(1).replace('.', ',')} %` : '—'}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })()}

                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="btn-outline flex-1"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary flex-1"
                                    >
                                        Enregistrer
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Dishes Table */}
                <div>
                    {loading ? (
                        <div className="text-center py-12 text-muted-foreground">
                            Chargement…
                        </div>
                    ) : filteredDishes.length === 0 ? (
                        <div className="text-center py-12">
                            <ChefHat className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
                            <p className="text-muted-foreground">
                                {searchTerm
                                    ? 'Aucun plat ne correspond.'
                                    : 'Aucun plat pour cette vue.'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="menu-list">
                                <div className="menu-head" aria-hidden="true">
                                    <span />
                                    <span className="text-left">Plat</span>
                                    <span>Prix TVAC</span>
                                    <span>Coût HTVA</span>
                                    <span>Marge</span>
                                    <span />
                                </div>
                                {paginatedDishes.map((dish) => {
                                    const sellPriceExVat = parseFloat(dish.selling_price_ex_vat || 0);
                                    const menuPrice = parseFloat(dish.selling_price || 0);
                                    const effectiveCost = parseFloat(dish.effective_cost || 0);
                                    const margin = parseFloat(dish.profit_margin || 0);
                                    const buttonColor = getButtonColor(dish.button_color);
                                    const hasRecipe = dish.dish_ingredients?.length > 0;
                                    const missingCost = hasRecipe && dish.dish_ingredients.some(di => parseFloat(di.ingredient_unit_price ?? 0) === 0);
                                    const priced = sellPriceExVat > 0;
                                    const done = priced && ((hasRecipe && !missingCost) || (dish.is_finished_product && effectiveCost > 0));
                                    const hasMargin = priced && effectiveCost > 0;
                                    const marginText = hasMargin ? `${margin.toFixed(1).replace('.', ',')} %` : '—';
                                    const advised = !priced && hasRecipe && !missingCost && isFood(dish) ? suggestedPrice(effectiveCost, parseFloat(dish.vat_rate), targetFoodCost) : null;
                                    const menuPriceText = menuPrice > 0 ? eur(menuPrice) : advised != null ? `≈ ${eur(advised)}` : '';
                                    const priceTitle = advised != null ? `Prix conseillé TVAC à ${targetFoodCost} % de coût matière` : undefined;

                                    return (
                                        <div key={dish.id} className="dish-line" onClick={() => setDetailDish(dish)}>
                                            <Plate share={hasMargin ? effectiveCost / sellPriceExVat : null} />
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); setDetailDish(dish); }} className="dish-name" title="Voir le détail du coût">
                                                        {dish.name}
                                                    </button>
                                                    {dish.button_color && (
                                                        <span
                                                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-border"
                                                            style={{ backgroundColor: buttonColor }}
                                                            title={`Caisse : ${dish.button_color}`}
                                                        />
                                                    )}
                                                    {done && <span className="text-good text-xs font-bold" title="Recette complète, coûts et prix définis">✓</span>}
                                                </div>
                                                <div className="mt-1 flex flex-wrap gap-1 empty:hidden">
                                                    {dish.is_finished_product ? (
                                                        <span className="tag text-muted-foreground">coût fixe</span>
                                                    ) : !hasRecipe && (
                                                        <span className="tag text-warn">sans recette</span>
                                                    )}
                                                    {missingCost && (
                                                        <span className="tag text-bad" title="Un ingrédient de la recette n'a pas de prix : coût sous-estimé">coût incomplet</span>
                                                    )}
                                                    {hasRecipe && !priced && <span className="tag text-bad">prix à définir</span>}
                                                    {dish.open_questions?.length > 0 && (
                                                        <span className="tag text-foreground" title={dish.open_questions.map(q => q.text).join(' / ')}>
                                                            {dish.open_questions.length} question{dish.open_questions.length > 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <span className={`dish-price ${advised != null ? 'is-advised' : ''}`} title={priceTitle}>{menuPriceText}</span>
                                            <span className={`dish-col-price ${advised != null ? 'is-advised' : ''}`} title={priceTitle}>{menuPriceText || '—'}</span>
                                            <span className="dish-col-cost">{effectiveCost > 0 ? eur(effectiveCost) : '—'}</span>
                                            <span className={`dish-col-margin ${hasMargin ? getMarginColor(margin) : 'text-muted-foreground'}`}>{marginText}</span>
                                            <div className="dish-numbers">
                                                <span>Coût HTVA <b className="text-foreground">{effectiveCost > 0 ? eur(effectiveCost) : '—'}</b></span>
                                                <span>Marge <b className={hasMargin ? getMarginColor(margin) : ''}>{marginText}</b></span>
                                            </div>
                                            <div className="dish-actions" onClick={(e) => e.stopPropagation()}>
                                                {dish.odoo_product_id && config.odooUrl && (
                                                    <a
                                                        href={`${config.odooUrl}/web#id=${dish.odoo_product_id}&model=product.product&view_type=form`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 text-muted-foreground hover:bg-accent rounded-full transition"
                                                        title="Ouvrir dans Odoo"
                                                    >
                                                        <ExternalLink className="h-4 w-4" />
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => handleEdit(dish)}
                                                    className="p-1.5 text-muted-foreground hover:bg-accent rounded-full transition"
                                                    title="Modifier"
                                                    aria-label={`Modifier ${dish.name}`}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(dish.id)}
                                                    className="p-1.5 text-bad hover:bg-accent rounded-full transition"
                                                    title="Supprimer"
                                                    aria-label={`Supprimer ${dish.name}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex flex-wrap items-center justify-between gap-2 pt-4">
                                    <div className="text-sm text-muted-foreground">
                                        {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredDishes.length)} sur {filteredDishes.length}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="p-1.5 rounded border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </button>
                                        <span className="text-sm text-foreground px-2">
                                            Page {currentPage} sur {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="p-1.5 rounded border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Dishes;
